import { AccountIconAndName } from "@components/Accounts/AccountPreviewCard/AccountIconAndName";
import { AccountDetailsChips } from "@components/Accounts/AccountPreviewCard/AccountDetailsChips";
import { UpdateConnectionButton } from "@components/Accounts";
import { Box, styled, Theme, useMediaQuery } from "@mui/material";
import { ExtendedAccount } from "@features/depots/types";
import React from "react";
import { useNavigate } from "react-router";
import { getUpdateBankConnectionUrl } from "@helpers";

const Container = styled(Box)<{ isLast: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(3)};
  gap: ${({ theme }) => theme.spacing(4)};
  cursor: pointer;
  ${({ isLast, theme }) =>
    !isLast && `border-bottom: 1px solid ${theme.palette.divider};`}
  ${({ theme }) => theme.breakpoints.down("sm")} {
    gap: ${({ theme }) => theme.spacing(2)};
    align-items: flex-start;
    flex-direction: column;
  }
`;

interface Props {
  account: ExtendedAccount;
  isLast: boolean;
}

export const DepotRow = ({ account, isLast }: Props) => {
  const navigate = useNavigate();
  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));

  const goToDepot = () => {
    navigate(`/depots/${account.id}`);
  };

  const updateConnectionUrl = getUpdateBankConnectionUrl(account);
  const canUpdate = account.syncRequired && Boolean(updateConnectionUrl);

  const onClickUpdate = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (updateConnectionUrl) navigate(updateConnectionUrl);
  };

  return (
    <Container
      isLast={isLast}
      onClick={goToDepot}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          goToDepot();
        }
      }}
      role="button"
      tabIndex={0}
    >
      <Box flex={1} minWidth={0}>
        <AccountIconAndName
          accountName={account.name}
          accountType={account.type}
          bankName={account.bankName}
          bankLogoURL={account.iconUrl}
          bankCode={account.accountNumber}
          content={
            canUpdate && !isSM ? (
              <Box mt={0.5}>
                <UpdateConnectionButton
                  onClick={onClickUpdate}
                  alwaysShowText
                />
              </Box>
            ) : undefined
          }
        />
      </Box>
      <AccountDetailsChips account={account} direction="column" />
      {canUpdate && isSM && (
        <UpdateConnectionButton onClick={onClickUpdate} alwaysShowText />
      )}
    </Container>
  );
};
